import React, { useState, useEffect } from 'react';
import './UserSettingsPage.scss';
import axios from 'axios';
import { useUser } from './../context/UserContext';
import { useAuth } from './../components/AuthContext'; 

interface UserDetails {
  gender: string;
  foodAllergies: string;
  dietaryPreferences: string[];
  likes: string[];
  dislikes: string[];
  mealFrequency: string;
}

interface PageProps {
  onCancel: () => void;
  onSave: (page: string) => void;
}

const UserSettingsPage: React.FC<PageProps> = ({ onCancel, onSave }) => {
  const { user } = useAuth();  // Use AuthContext to get the user
  const [userid, setUserid] = useState<string | null>(null);  // Local state to store userid
  const [userDetails, setUserDetails] = useState<UserDetails>({
    gender: '',
    foodAllergies: '',
    dietaryPreferences: [],
    likes: [],
    dislikes: [],
    mealFrequency: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch user settings from the API when the component mounts
  useEffect(() => {
    if (user) {
      
      user.getUserAttributes((err, attributes) => {
        if (err) {
          console.error('Failed to get user attributes', err);
        } else {
          const userIdFromAttributes = attributes?.find(attr => attr.Name === 'sub')?.Value;
          if (userIdFromAttributes) {
            setUserid(userIdFromAttributes);  // Set the userid in local state
            
            // Fetch user settings once userid is available
            fetchUserSettings(userIdFromAttributes);
          }
        }
      });
    }
  }, [user]);

  const fetchUserSettings = async (userid: string) => {
    try {
      const apiUrl = `https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields`;
      const response = await axios.post(apiUrl, {
        entity: 'userdetails',
        action: 'read',
        pk: 'userid',
        userid: userid,
      });

      let parsedData;

      // Check if response.data.body is a string
      if (typeof response.data.body === 'string') {
          try {
              parsedData = JSON.parse(response.data.body);
          } catch (error) {
              console.error('Error parsing JSON:', error);
              parsedData = null;
          }
      } else {
          // If it's already an object, assign it directly
          parsedData = response.data.body;
      }
      console.log(parsedData);
      if (parsedData && parsedData.userdetails) {
          const data = parsedData.userdetails.userinfo;

          setUserDetails({
              gender: data.gender || '',
              foodAllergies: data.foodAllergies || '',
              dietaryPreferences: data.dietaryPreferences ? data.dietaryPreferences.split(', ') : [],
              likes: data.likes ? data.likes.split(', ') : [],
              dislikes: data.dislikes ? data.dislikes.split(', ') : [],
              mealFrequency: data.mealFrequency || '',
          });
      } else {
          console.error("userDetails not found in parsed data");
      }

    } catch (error) {
      setError('Error fetching user settings');
      alert('Error fetching user settings: ' + error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
    setIsDirty(true);
  };

  const handleMultiSelectChange = (name: string, value: string[]) => {
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
    setIsDirty(true);
  };

  const handleSubmit = async () => {
    const columns = {
        userid: userid,
        LastLoginTimestamp: Math.floor(Date.now() / 1000).toString(),
        gender: userDetails.gender,
        foodAllergies: userDetails.foodAllergies,
        dietaryPreferences: userDetails.dietaryPreferences.join(', '),
        likes: userDetails.likes.join(', '),
        dislikes: userDetails.dislikes.join(', '),
        mealFrequency: userDetails.mealFrequency,
    };

    const payload = {
        entity: 'userdetails',
        action: 'upsert',
        pk: 'userid',
        columns: columns,
    };

    alert('Saving user settings...' + JSON.stringify(payload));

    const apiUrl = 'https://dw1271o3wa.execute-api.us-east-1.amazonaws.com/v1/tablefields';

    try {
        const response = await axios.post(apiUrl, payload, {
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.status === 200) {
            console.log('Success:', response.data);
            setIsDirty(false); // Reset the dirty state after saving
            onSave('/'); // Navigate to the home page after saving
        } else {
            console.error('Unexpected response status:', response.status, response.data);
            setError('Unexpected error occurred. Please try again later.');
        }
    } catch (error) {
        if (axios.isAxiosError(error)) {
            // Narrowing the error type to AxiosError
            if (error.response) {
                // Server responded with a status other than 2xx
                console.error('Error response:', error.response.status, error.response.data);
                setError(`Failed to save user settings: ${error.response.data.message || 'Server error'}.`);
            } else if (error.request) {
                // Request was made but no response was received
                console.error('No response received:', error.request);
                setError('Failed to save user settings: No response from server.');
            } else {
                // Something else happened while setting up the request
                console.error('Error:', error.message);
                setError('Failed to save user settings: ' + error.message);
            }
        } else {
            // Handling non-Axios errors
            console.error('Unknown error:', error);
            setError('An unexpected error occurred. Please try again.');
        }
    }
};


  const handleCancelClick = () => {
    if (isDirty) {
      const confirmLeave = window.confirm('You have unsaved changes. Do you want to save them before leaving?');
      if (confirmLeave) {
        handleSubmit(); // Save changes before navigating away
      } else {
        onCancel(); // Navigate without saving
      }
    } else {
      onSave('/'); // Navigate to the home page directly if no changes
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="user-settings-page">
      <h1>User Preferences</h1>
      <div className="form">
        <label>
          Gender:
          <select
            name="gender"
            value={userDetails.gender}
            onChange={handleInputChange}
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          Food Allergies:
          <input
            type="text"
            name="foodAllergies"
            value={userDetails.foodAllergies}
            onChange={handleInputChange}
          />
        </label>
        <label>
          Dietary Preferences:
          <select
            name="dietaryPreferences"
            multiple
            value={userDetails.dietaryPreferences}
            onChange={(e) =>
              handleMultiSelectChange(
                'dietaryPreferences',
                Array.from(e.target.selectedOptions, (option) => option.value)
              )
            }
          >
            <option value="vegetarian">Vegetarian</option>
            <option value="gluten-free">Gluten-free</option>
            <option value="dairy-free">Dairy-free</option>
            <option value="vegan">Vegan</option>
          </select>
        </label>
        <label>
          Likes:
          <input
            type="text"
            name="likes"
            value={userDetails.likes.join(', ')}
            onChange={(e) => handleMultiSelectChange('likes', e.target.value.split(', '))}
          />
        </label>
        <label>
          Dislikes:
          <input
            type="text"
            name="dislikes"
            value={userDetails.dislikes.join(', ')}
            onChange={(e) => handleMultiSelectChange('dislikes', e.target.value.split(', '))}
          />
        </label>
        <label>
          Meal Frequency:
          <select
            name="mealFrequency"
            value={userDetails.mealFrequency}
            onChange={handleInputChange}
          >
            <option value="">Select</option>
            <option value="3 meals a day">3 meals a day</option>
            <option value="2 meals a day">2 meals a day</option>
            <option value="1 meal a day">1 meal a day</option>
          </select>
        </label>

        <div className="buttons">
          <button onClick={handleSubmit}>Save</button>
          <button onClick={handleCancelClick}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsPage;
